/**
 * `/tasks` — the work a person owes, across every project and every workspace
 * (`MCK-20260521-009`, owner 2026-09-12 and 2026-09-13).
 *
 * **Why this page carries weight.** Since D3 (2026-09-13) L0 sends no notification email: a task
 * assigned to you, a review requested of you and a mention land here and nowhere else. Since
 * 2026-09-17 (owner) it is also where every sign-in begins for anybody who reaches a project,
 * admin included, and the first item in the rail (`HomeRoute`, `LeftRail`).
 * So the page answers exactly one question — *what do I owe* — and answers it without a project,
 * a slicer or a saved filter in the way.
 *
 * FOUR DECISIONS
 *
 * 1. **The register's shape, over every project.** One server-filtered list on the keyset cursor
 *    (`GET /api/v1/tasks`, newest first), rows arriving on the scroll and only the visible ones
 *    drawn (`lib/infinite.ts`, `lib/windowing.ts`) — the same machinery `TaskRegister` uses, for
 *    the same reasons, rather than a second opinion about how a long list behaves.
 * 2. **The shared `FilterBar`** (`KLQ-20260910-001`: one toolbar on every list surface), with
 *    only the fields the endpoint declares — kind, status, due and resolved. The state is the URL
 *    (`filters.ts`, `MyTaskFilters`), and the default question is *open and in progress*.
 * 3. **The project name is a join, not a field.** `TaskResponse.project_id` is on every row, and
 *    B1's project list is already in the cache under B1's own key; a request per row, or a twin
 *    schema carrying a name, would both cost more than reading what is held. A project the list
 *    has not loaded yet pulls its next page, which is bounded by how many projects the caller
 *    can see at all.
 * 4. **Loading is a skeleton, never a spinner** (`CLAUDE.md`, loading states): the layout is
 *    known before the rows are, so its shape is drawn — five rows, a modest guess — and an empty
 *    answer replaces it with one sentence rather than walking back six shadows.
 *
 * **Not here, deliberately.** No studio chip: `TaskResponse` carries neither a workspace id nor
 * an update time, and a chip inferred from anything else would be a guess about which company a
 * row belongs to. No company-assigned work: `assignee_party_id` is the project's *My company*
 * queue, and this page is about the person.
 */

import { useEffect, useState } from "react";

import { useNavigate } from "@tanstack/react-router";

import {
  Button,
  ChipSelect,
  EmptyState,
  InlineError,
  Input,
  Screen,
  ScreenHead,
  Select,
  SkeletonRows,
} from "@/components";
import type { ChipSelectOption, FilterChip } from "@/components";
import { FilterBar } from "@/components/filter-bar";
import { DEFAULT_PROJECT_SORT, useProjectList } from "@/features/projects";
import { useActiveWorkspaceId, useCompanies } from "@/features/session";
import { useStudioAuthority, useWorkspaceMembers } from "@/features/studio";
import { useLocale, useMessages } from "@/lib/i18n";
import { useInfiniteSentinel } from "@/lib/infinite";
import { useMediaQuery } from "@/lib/media";
import { useAnchoredWindow } from "@/lib/windowing";

import type { TaskKind, TaskStatus } from "./api";
import { TASK_KINDS, TASK_STATUSES } from "./api";
import type { MyTaskFilters } from "./filters";
import {
  dayOfDueFrom,
  dayOfDueUntil,
  dueFromDay,
  dueUntilDay,
  isMyTasksNarrowed,
  MY_TASKS_DEFAULT,
  MY_TASKS_DEFAULT_STATUS,
  searchFromMyFilters,
} from "./filters";
import { LoadedCount } from "./LoadedCount";
import { MyTaskRow } from "./MyTaskRow";
import styles from "./MyTasks.module.css";
import { detailOf, taskErrorKey } from "./problems";
import { useMyTaskList } from "./queries";
import { KIND_KEY } from "./kinds";
import { STATUS_KEY } from "./TaskRegister";
import taskStyles from "./Tasks.module.css";

/** One row, in pixels. TWIN: `--my-task-row-h` in `MyTasks.module.css`. */
const MY_TASK_ROW_HEIGHT = 44;
/**
 * One **card**, in pixels — what a row becomes under 720px (owner, 2026-09-16: *"môžu to byť
 * kartičky tak, aby to aj na telefóne bolo čitateľné"*).
 *
 * The window needs a number and the stylesheet needs the same number, so the phone has its own
 * pair. TWIN: `--my-task-card-h` in `MyTasks.module.css`.
 */
const MY_TASK_CARD_HEIGHT = 112;
/** TWIN: the 720px in `MyTasks.module.css`, and the app chrome's own breakpoint. */
const PHONE = "(max-width: 720px)";

/** The skeleton's guess. Modest on purpose — at most six (`CLAUDE.md`, loading states). */
const SKELETON_ROWS = 5;

export interface MyTasksPageProps {
  readonly filters: MyTaskFilters;
}

export function MyTasksPage({ filters }: MyTasksPageProps) {
  const t = useMessages("tasks");
  const { locale } = useLocale();
  // The studio chip's two inputs. A chip is due only when there is more than one company to
  // tell apart, and only on rows from a company other than the one this tab acts as.
  const companies = useCompanies();
  const activeWorkspaceId = useActiveWorkspaceId();
  const studioOf = (workspaceId: string): string | null => {
    if (companies.length < 2 || workspaceId === activeWorkspaceId) return null;
    return companies.find((company) => company.id === workspaceId)?.name ?? null;
  };
  const navigate = useNavigate();
  /**
   * **Whose work this page may ask about.** Admin and manager only, and the server refuses the
   * rest — this decides whether the control is *drawn*, never what the answer contains
   * (retro rule 1: the authority half is `service.list_my_tasks`).
   */
  const studio = useStudioAuthority();
  const colleagues = useWorkspaceMembers(studio.allowed);
  const nameOf = new Map(
    (colleagues.data?.items ?? []).map(
      (member) =>
        [member.user_id, member.display_name ?? member.email ?? t("row.unassigned")] as const,
    ),
  );
  const showingOthers = filters.everyone || filters.people.length > 0;
  const list = useMyTaskList(filters);
  // B1's query, under B1's key — `all` and B1's default sort — so this reads the cache entry the
  // projects list already filled rather than issuing a second request for the same rows.
  const projects = useProjectList({ status: "all", sort: DEFAULT_PROJECT_SORT });
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const apply = (next: MyTaskFilters): void => {
    void navigate({ to: "/tasks", search: searchFromMyFilters(next) });
  };

  const tasks = list.data?.pages.flatMap((page) => page.items) ?? [];
  const hasMore = list.data?.pages.at(-1)?.has_more ?? false;
  const narrowed = isMyTasksNarrowed(filters);

  const names = new Map(
    (projects.data?.pages ?? []).flatMap((page) =>
      page.items.map((project) => [project.id, project.name] as const),
    ),
  );
  const unnamed = tasks.some((task) => !names.has(task.project_id));

  // A row whose project the loaded list does not name pulls the list's next page — once per
  // page, and never while one is already on its way. It stops when the list runs out, and a
  // project still unnamed after that reads as the generic word rather than as an id.
  const { hasNextPage: moreProjects, isFetchingNextPage: fetchingProjects } = projects;
  const fetchMoreProjects = projects.fetchNextPage;
  useEffect(() => {
    if (unnamed && moreProjects && !fetchingProjects) void fetchMoreProjects();
  }, [unnamed, moreProjects, fetchingProjects, fetchMoreProjects]);

  useInfiniteSentinel(sentinel, {
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  const phone = useMediaQuery(PHONE);
  const view = useAnchoredWindow(tasks.length, phone ? MY_TASK_CARD_HEIGHT : MY_TASK_ROW_HEIGHT);
  const drawn = tasks.slice(view.window.first, view.window.last);

  const kind = chipsOf(TASK_KINDS, filters.kind, (value) => t(KIND_KEY[value]));
  const status = chipsOf(TASK_STATUSES, filters.status, (value) => t(STATUS_KEY[value]));

  /**
   * One chip per narrowing — **and the default status set is not one.** The bare page asks for
   * open and in progress, and a chip row drawn on every visit would make the unfiltered page
   * look filtered and offer a *Clear* that changes nothing. The panel still shows both statuses
   * held, which is where somebody looking for their finished work finds why it is absent.
   */
  const summary: FilterChip[] = [
    // Whose work first: it is the biggest thing the page can be narrowed by, and an answer
    // that is somebody else's work must never look like the reader's own.
    ...(filters.everyone
      ? [
          {
            id: "everyone",
            field: "who",
            label: t("mine.who.everyone"),
            onRemove: () => {
              apply({ ...filters, everyone: false });
            },
          },
        ]
      : filters.people.map((userId) => ({
          id: `who:${userId}`,
          field: "who",
          label: nameOf.get(userId) ?? t("row.unassigned"),
          onRemove: () => {
            apply({ ...filters, people: filters.people.filter((one) => one !== userId) });
          },
        }))),
    ...filters.kind.map((value) => ({
      id: `kind:${value}`,
      field: "kind",
      label: t(KIND_KEY[value]),
      onRemove: () => {
        apply({ ...filters, kind: filters.kind.filter((one) => one !== value) });
      },
    })),
    ...(sameMembers(filters.status, MY_TASKS_DEFAULT_STATUS)
      ? []
      : filters.status.map((value) => ({
          id: `status:${value}`,
          field: "status",
          label: t(STATUS_KEY[value]),
          onRemove: () => {
            apply({ ...filters, status: filters.status.filter((one) => one !== value) });
          },
        }))),
    ...(filters.dueAfter === null
      ? []
      : [
          {
            id: "dueAfter",
            field: "dueAfter",
            label: `${t("filter.dueFrom")}: ${dayOfDueFrom(filters.dueAfter)}`,
            onRemove: () => {
              apply({ ...filters, dueAfter: null });
            },
          },
        ]),
    ...(filters.dueBefore === null
      ? []
      : [
          {
            id: "dueBefore",
            field: "dueBefore",
            label: `${t("filter.dueUntil")}: ${dayOfDueUntil(filters.dueBefore)}`,
            onRemove: () => {
              apply({ ...filters, dueBefore: null });
            },
          },
        ]),
    ...(filters.resolved === null
      ? []
      : [
          {
            id: "resolved",
            field: "resolved",
            label: t(filters.resolved ? "filter.resolvedYes" : "filter.resolvedNo"),
            onRemove: () => {
              apply({ ...filters, resolved: null });
            },
          },
        ]),
  ];

  return (
    <Screen
      head={
        <ScreenHead
          level={1}
          title={
            filters.everyone
              ? t("mine.title.everyone")
              : filters.people.length === 0
                ? t("mine.title")
                : t("mine.title.people", { count: filters.people.length })
          }
          meta={list.isSuccess ? <LoadedCount count={tasks.length} hasMore={hasMore} /> : undefined}
        />
      }
      toolbar={
        <FilterBar
          legend={t("filter.legend")}
          triggerLabel={t("filter.open")}
          panelLabel={t("filter.legend")}
          closeLabel={t("filter.close")}
          summary={summary}
          clearLabel={t("filter.clear")}
          removeLabel={(name) => t("filter.remove", { name })}
          onClear={() => {
            // Back to the page's own question, not to "everything": clearing is undoing what the
            // person narrowed, and the default was never theirs to have narrowed.
            apply(MY_TASKS_DEFAULT);
          }}
        >
          {/* Drawn for somebody who runs the studio and for nobody else. A control whose only
              outcome is `403` is not a control (`KLQ-20260819-008` §2's rule, one screen over),
              and the question *whose work* is meaningless to a person who may only see theirs. */}
          {studio.allowed ? (
            <div className={taskStyles.panelField}>
              <span className={taskStyles.panelLabel}>{t("mine.who")}</span>
              <ChipSelect
                size="sm"
                tokens={filters.people.map((userId) => ({
                  id: userId,
                  label: nameOf.get(userId) ?? t("row.unassigned"),
                }))}
                options={(colleagues.data?.items ?? [])
                  .filter((member) => !filters.people.includes(member.user_id))
                  .map((member) => ({
                    id: member.user_id,
                    label: nameOf.get(member.user_id) ?? t("row.unassigned"),
                  }))}
                addLabel={t("mine.who.add")}
                removeLabel={(name) => t("filter.remove", { name })}
                onAdd={(picked) => {
                  // Naming a person turns *the whole studio* off: the two are different
                  // questions and the URL holds only one of them.
                  apply({ ...filters, everyone: false, people: [...filters.people, picked] });
                }}
                onRemove={(removed) => {
                  apply({
                    ...filters,
                    people: filters.people.filter((one) => one !== removed),
                  });
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  apply(
                    filters.everyone
                      ? { ...filters, everyone: false }
                      : { ...filters, everyone: true, people: [] },
                  );
                }}
              >
                {t(filters.everyone ? "mine.who.mineAgain" : "mine.who.everyoneAction")}
              </Button>
            </div>
          ) : null}
          <div className={taskStyles.panelField}>
            <span className={taskStyles.panelLabel}>{t("filter.kind")}</span>
            <ChipSelect
              size="sm"
              tokens={kind.tokens}
              options={kind.options}
              addLabel={t("filter.kindAny")}
              removeLabel={(name) => t("filter.remove", { name })}
              onAdd={(picked) => {
                apply({
                  ...filters,
                  kind: TASK_KINDS.filter((one) => filters.kind.includes(one) || one === picked),
                });
              }}
              onRemove={(removed) => {
                apply({ ...filters, kind: filters.kind.filter((one) => one !== removed) });
              }}
            />
          </div>
          <div className={taskStyles.panelField}>
            <span className={taskStyles.panelLabel}>{t("filter.status")}</span>
            <ChipSelect
              size="sm"
              tokens={status.tokens}
              options={status.options}
              addLabel={t("filter.statusAny")}
              removeLabel={(name) => t("filter.remove", { name })}
              onAdd={(picked) => {
                apply({
                  ...filters,
                  status: TASK_STATUSES.filter(
                    (one) => filters.status.includes(one) || one === picked,
                  ),
                });
              }}
              onRemove={(removed) => {
                apply({ ...filters, status: filters.status.filter((one) => one !== removed) });
              }}
            />
          </div>
          <div className={taskStyles.panelField}>
            <Input
              type="date"
              size="sm"
              label={t("filter.dueFrom")}
              value={dayOfDueFrom(filters.dueAfter)}
              onChange={(event) => {
                apply({ ...filters, dueAfter: dueFromDay(event.target.value) });
              }}
            />
          </div>
          <div className={taskStyles.panelField}>
            <Input
              type="date"
              size="sm"
              label={t("filter.dueUntil")}
              value={dayOfDueUntil(filters.dueBefore)}
              onChange={(event) => {
                apply({ ...filters, dueBefore: dueUntilDay(event.target.value) });
              }}
            />
          </div>
          <div className={taskStyles.panelField}>
            <Select
              label={t("filter.resolved")}
              size="sm"
              value={filters.resolved === null ? "" : String(filters.resolved)}
              onChange={(event) => {
                const picked = event.target.value;
                apply({ ...filters, resolved: picked === "" ? null : picked === "true" });
              }}
            >
              <option value="">{t("filter.any")}</option>
              <option value="true">{t("filter.resolvedYes")}</option>
              <option value="false">{t("filter.resolvedNo")}</option>
            </Select>
          </div>
        </FilterBar>
      }
    >
      <div className={styles.page}>
        {list.isError ? (
          <InlineError variant="block">
            {detailOf(list.error) ?? t(taskErrorKey(list.error))}
          </InlineError>
        ) : list.isPending ? (
          <SkeletonRows
            label={t("mine.loading")}
            rows={SKELETON_ROWS}
            rowHeight={MY_TASK_ROW_HEIGHT}
            columns={[16, "34%", "18%", 64]}
          />
        ) : tasks.length === 0 ? (
          // One sentence, and which one is the difference between good news and a statement
          // about the filter.
          //
          // The unfiltered one carries the way to the projects list (owner, 2026-09-17). This
          // page is the front door for everybody who reaches a project, so an empty answer here
          // is often an admin on a quiet day or somebody just added — and the list of projects is
          // where either goes next. The filtered one does not: its answer is the filter panel.
          <EmptyState
            variant="list"
            explains={t("mine.explains")}
            title={t(narrowed ? "mine.emptyFiltered.title" : "mine.empty.title")}
            body={t(narrowed ? "mine.emptyFiltered" : "mine.empty")}
            action={
              narrowed ? undefined : (
                <Button
                  size="sm"
                  onClick={() => {
                    void navigate({ to: "/projects" });
                  }}
                >
                  {t("mine.empty.action")}
                </Button>
              )
            }
          />
        ) : (
          <>
            <div
              className={[styles.columns, showingOthers ? styles.columnsWithWho : ""]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            >
              <span>{t("mine.column.task")}</span>
              {showingOthers ? <span>{t("mine.column.who")}</span> : null}
              <span>{t("mine.column.project")}</span>
              <span>{t("mine.column.status")}</span>
              <span className={styles.wide}>{t("mine.column.due")}</span>
              <span className={styles.wide}>{t("mine.column.raised")}</span>
            </div>
            <ol className={styles.list} ref={view.ref} aria-label={t("mine.title")}>
              {view.window.above > 0 ? (
                <li aria-hidden style={{ height: `${String(view.window.above)}px` }} />
              ) : null}
              {drawn.map((task) => (
                <li key={task.id}>
                  <MyTaskRow
                    task={task}
                    projectName={names.get(task.project_id) ?? t("mine.projectUnavailable")}
                    studio={studioOf(task.workspace_id)}
                    // Who owes it — drawn only while the page is about somebody other than the
                    // reader. On their own list every row is theirs, and a column repeating
                    // their own name on every line says nothing.
                    {...(showingOthers
                      ? {
                          who:
                            task.assignee_user_ids.length === 0
                              ? t("row.unassigned")
                              : task.assignee_user_ids
                                  .map((userId) => nameOf.get(userId) ?? t("row.unassigned"))
                                  .join(", "),
                        }
                      : {})}
                    t={t}
                    locale={locale}
                  />
                </li>
              ))}
              {view.window.below > 0 ? (
                <li aria-hidden style={{ height: `${String(view.window.below)}px` }} />
              ) : null}
            </ol>
          </>
        )}

        {/* The next page arrives on the scroll; the button is the keyboard's way to it. */}
        {list.hasNextPage ? (
          <div ref={setSentinel} className={styles.sentinel}>
            <Button
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {list.isFetchingNextPage ? t("list.loadingMore") : t("list.more")}
            </Button>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}

function sameMembers<T extends string>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/** Held values as chips, the rest as the picker's rows — both in the field's own order. */
function chipsOf<T extends TaskKind | TaskStatus>(
  all: readonly T[],
  members: readonly T[],
  label: (value: T) => string,
): { readonly tokens: readonly ChipSelectOption[]; readonly options: readonly ChipSelectOption[] } {
  const chip = (value: T): ChipSelectOption => ({ id: value, label: label(value) });
  return {
    tokens: all.filter((value) => members.includes(value)).map(chip),
    options: all.filter((value) => !members.includes(value)).map(chip),
  };
}
